import axios from "axios";
import { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { Card, Table, Tag, Typography, Tabs, Button, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Title } = Typography;

const STATUS_COLORS = {
    "Pending Approval": "orange",
    Available: "green",
    Rejected: "red",
    Availed: "blue",
    Expired: "default",
    PartiallyUsed: "gold",
    Cancelled: "default",
};

// Group raw statuses into the 3 tabs
const STATUS_GROUPS = {
    pending: ["Pending Approval"],
    approved: ["Available", "Availed", "PartiallyUsed", "Expired", "Cancelled"],
    rejected: ["Rejected"],
};

export default function MyCompoffs() {
    const [compoffList, setCompoffList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("pending");
    const { authData, backUrl } = useContext(AuthContext);
    const navigate = useNavigate();

    const fetchMyCompoffs = async () => {
        if (!authData?.employee?.id) {
            message.warning("Unable to determine logged-in employee.");
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(`${backUrl}/api/hr/compoffs/`, {
                params: { created_by: authData.employee.id },
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setCompoffList(res.data?.results || []);
        } catch (err) {
            message.error("Failed to fetch comp-off records.");
            setCompoffList([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMyCompoffs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ Split records by status group
    const grouped = useMemo(() => {
        const result = { pending: [], approved: [], rejected: [] };
        compoffList.forEach((item) => {
            const status = item.status;
            if (STATUS_GROUPS.pending.includes(status)) {
                result.pending.push(item);
            } else if (STATUS_GROUPS.rejected.includes(status)) {
                result.rejected.push(item);
            } else if (STATUS_GROUPS.approved.includes(status)) {
                result.approved.push(item);
            }
        });
        return result;
    }, [compoffList]);

    const columns = [
        {
            title: "Employee Code",
            dataIndex: ["employee", "emp_code"],
            key: "emp_code",
            ellipsis: true,
        },
        {
            title: "Employee Name",
            dataIndex: ["employee", "full_name"],
            key: "full_name",
            ellipsis: true,
        },
        {
            title: "Organisation",
            dataIndex: ["employee", "organisation_name"],
            key: "organisation_name",
            ellipsis: true,
        },
        {
            title: "Department",
            dataIndex: ["employee", "department_name"],
            key: "department_name",
            ellipsis: true,
        },
        {
            title: "Designation",
            dataIndex: ["employee", "designation_name"],
            key: "designation_name",
            ellipsis: true,
        },
        {
            title: "Work Date",
            dataIndex: "work_date",
            key: "work_date",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
            ellipsis: true,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag color={STATUS_COLORS[status] || "default"}>{status || "-"}</Tag>
            ),
        },
        {
            title: "Days Available",
            dataIndex: "days_available",
            key: "days_available",
        },
        {
            title: "Expiry Date",
            dataIndex: "expiry_date",
            key: "expiry_date",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
    ];

    const renderTable = (data) => (
        <Table
            columns={columns}
            dataSource={data}
            rowKey={(record) => record.id}
            loading={loading}
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
        />
    );

    const tabItems = [
        {
            key: "pending",
            label: `Pending (${grouped.pending.length})`,
            children: renderTable(grouped.pending),
        },
        {
            key: "approved",
            label: `Approved (${grouped.approved.length})`,
            children: renderTable(grouped.approved),
        },
        {
            key: "rejected",
            label: `Rejected (${grouped.rejected.length})`,
            children: renderTable(grouped.rejected),
        },
    ];

    return (
        <div style={{ padding: "24px" }}>
            <Card
                style={{
                    borderRadius: 12,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 16,
                    }}
                >
                    {/* <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(-1)}
                    >
                        Back
                    </Button> */}
                    <Title level={4} style={{ color: "#0e497b", margin: 0 }}>
                        My Comp-Off Requests
                    </Title>
                    <div style={{ width: 82 }} /> {/* spacer to keep title centered */}
                </div>

                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                />
            </Card>
        </div>
    );
}